"use client";

import { Box, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { CONTENT_ITEM_TEXT_PROPS } from "@/shared/ui/display/ContentItemPrimitives";

type HighlightedTextPart = {
  key: string;
  text: string;
  isHighlighted: boolean;
};

type OriginHighlightedTextProps = {
  searchQuery?: string;
  text: string;
};

const MIN_HIGHLIGHT_TERM_LENGTH = 2;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getSearchTerms(searchQuery: string) {
  const terms = searchQuery
    .trim()
    .split(/\s+/)
    .map((term) => term.replace(/^["'`]+|["'`]+$/g, ""))
    .filter(
      (term) => term.length >= MIN_HIGHLIGHT_TERM_LENGTH && !term.includes(":"),
    );

  return Array.from(new Set(terms));
}

function getHighlightedTextParts(
  text: string,
  searchQuery?: string,
): HighlightedTextPart[] {
  const terms = getSearchTerms(searchQuery ?? "");

  if (terms.length === 0) {
    return [{ key: "plain", text, isHighlighted: false }];
  }

  const highlightPattern = new RegExp(
    `(${terms.map(escapeRegExp).join("|")})`,
    "gi",
  );

  return text
    .split(highlightPattern)
    .filter((part) => part.length > 0)
    .map((part, index) => ({
      key: `${index}-${part}`,
      text: part,
      isHighlighted: terms.some(
        (term) => term.toLocaleLowerCase() === part.toLocaleLowerCase(),
      ),
    }));
}

export default function OriginHighlightedText({
  searchQuery,
  text,
}: OriginHighlightedTextProps) {
  const textParts = useMemo(
    () => getHighlightedTextParts(text, searchQuery),
    [searchQuery, text],
  );

  return (
    <Text color="moveon.surface.fg" {...CONTENT_ITEM_TEXT_PROPS}>
      {textParts.map((part) =>
        part.isHighlighted ? (
          <Box
            key={part.key}
            as="mark"
            bg="moveon.warningLight"
            color="moveon.surface.fg"
            rounded="sm"
            px="0.5"
          >
            {part.text}
          </Box>
        ) : (
          part.text
        ),
      )}
    </Text>
  );
}
